import { onAuthStateChanged } from "@firebase/auth";
import imageCompression from "browser-image-compression";
import {
  type ActionCodeSettings,
  deleteUser,
  fetchSignInMethodsForEmail,
  getAuth,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
  type User,
} from "firebase/auth";
import {
  addDoc,
  collection,
  type DocumentData,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore/lite";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
// import { Loader, Box } from "senf-atomic-design-system";
import type { FormikValues } from "formik";
import React, { useEffect } from "react";
import {
  isChrome,
  isDesktop,
  isFirefox,
  isMobileOnly,
  isTablet,
} from "react-device-detect";
import { generateErrorMessage } from "../firebase/generateErrorMessage";
import { useCreateUserWithEmailAndPassword } from "../firebase/useCreateUserWithEmailAndPassword";
import { useHandleSubmitEditDetails } from "../firebase/useHandleSubmitEditDetails";
import { useSignIn } from "../firebase/useSignInWithPopup";
import type { Project } from "../types";
import { ifAllUserDetailsAreFilled } from "../utils/ifAllUserDetailsAreFilled";
import { auth, db } from "./firebase";
import {
  deleteComment,
  getUserComments,
  getUserIdeas,
  getUserLikes,
  getUserOrganizations,
  getUserSurveyEntries,
  hideIdea,
  submitComment,
  unHideIdea,
} from "./providerFunctions";

export interface AuthState {
  user: (DocumentData & User) | null;
  setUser: (user: (DocumentData & User) | null) => void;
  loading: boolean | string;
  setLoading: (loading: boolean | string) => void;
  errorMessage: string;
}
export interface AuthMethods {
  signIn: (data: {
    provider?: string;
    formikStore?: FormikValues;
    id?: string;
    project?: Project;
  }) => Promise<User | DocumentData>;
  signOut: () => Promise<void>;
  deleteUserFromDb: (
    userId: string,
  ) => Promise<
    | { success: boolean; error?: undefined }
    | { error: string; success?: undefined }
  >;
  createUser: (formikStore: FormikValues) => Promise<User | DocumentData>;
  sendPasswordResetEmail: (
    email: string,
    actionCodeSettings?: ActionCodeSettings | undefined,
  ) => Promise<void>;
  sendEmailVerification: typeof sendEmailVerification;
  userExists: (formikStore: FormikValues, login?: boolean) => Promise<string[]>;
  submitEditDetails: (
    formikStore: FormikValues,
  ) => Promise<User | DocumentData>;
  handleImageUpload: (event: Event, user?: DocumentData) => Promise<string>;
  ifAllUserDetailsAreFilled: (user?: DocumentData) => boolean;

  // do we need a separate context for these methods?
  getUserData: (
    userId: string,
    project?: Project,
    participant?: DocumentData,
  ) => Promise<DocumentData>;
  getUserIdeas: (project: any, userId: string) => Promise<DocumentData[]>;
  getUserOrganizations: (
    userId: string,
    isSuperAdmin: boolean,
  ) => Promise<DocumentData[]>;
  submitComment: (
    ideaId: string,
    commentData: { body: string },
    user: User,
    device: "mobile" | "tablet" | "desktop" | "other",
  ) => Promise<{
    commentId: string | null;
    title: string | null;
    createdAt: string;
    ideaId: string;
    userHandle: string | null;
    photoURL: string | null;
    userId: string;
    gender: string | null;
    age: number | null;
    topic: string;
  }>;
  deleteComment: (
    commentId: string,
    user: User,
    ideaId: string,
  ) => Promise<{
    commentId: string;
  }>;
  handleLike: (
    ideaId: string,
  ) => Promise<false | { ideaId: string; userHandle: string; userId: string }>;
}
export interface AuthContext extends AuthState, AuthMethods {}

const AuthContext = React.createContext<AuthContext>({});

const AuthProvider = ({ children }) => {
  const [user, setUser] = React.useState<DocumentData | null>(null);
  const blankError = "";
  const [errorMessage, setErrorMessage] = React.useState("");
  const [loading, setLoading] = React.useState<boolean | string>(true);
  const handleSetErrorMessage = (error: Error) => {
    setErrorMessage(generateErrorMessage(error?.code ?? error?.message));
  };

  useEffect(() => {
    if (user?.userId) {
      if (window.Cypress) {
        console.log("CYPRESS USER", user);
        window.user = user;
      }
    }
  }, [user, window?.Cypress]);

  // Methods

  /**
   * @params participant this is used for when user data was changed that is otherwise only saved in the project, which has not been updated yet
   */
  const getUserData = async (
    userId: string,
    project?: Project,
    participant?: DocumentData,
  ) => {
    // trying to get any user data from firestore
    if (!userId) throw new Error("No user id provided to getUserData");
    const auth = getAuth();
    const { currentUser } = auth;
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("userId", "==", userId));
    const snapshot = await getDocs(q);
    const userData = snapshot?.docs[0]?.data();
    if (!userData) {
      throw new Error("No user data found in getUserData() AuthProvider");
    }
    userData.likes = [];
    userData.comments = [];
    userData.surveyEntries = [];
    userData.organizations = [];

    if (project?.organizationId) {
      const memberRef = doc(
        db,
        `organizations/${project.organizationId}/members/${userId}`,
      );
      const memberDoc = await getDoc(memberRef);
      const memberData = memberDoc.data();

      const projectMemberRef = doc(
        db,
        `organizations/${project.organizationId}/projects/${project.projectId}/members/${userId}`,
      );
      const projectMemberDoc = await getDoc(projectMemberRef);
      const isProjectMember = projectMemberDoc.exists();

      // Set admin status based on organization role
      userData.isAdmin =
        memberData?.role === "admin" ||
        memberData?.role === "editor" ||
        isProjectMember ||
        (memberData && !project?.settings?.accessLimitations) ||
        userData?.isSuperAdmin;
    }

    const isEmailVerified = () => {
      // https://stackoverflow.com/questions/38398545/firebase-facebook-auth-email-verified-always-false
      // thats why I am setting it to true if the user is logged in with facebook
      if (currentUser?.providerData[0]?.providerId === "facebook.com") {
        return true;
      }
      return currentUser?.emailVerified;
    };
    if (currentUser?.uid === userId) {
      // attach these properties to logged in user
      userData.profileCompleted = ifAllUserDetailsAreFilled(
        userData,
        participant,
        project,
      );
      userData.email = currentUser?.email;
      userData.emailVerified = !!isEmailVerified();
      userData.authenticated = !!isEmailVerified() || currentUser.isAnonymous;

      const results = await Promise.allSettled([
        getUserLikes(userId),
        getUserComments(userId),
        getUserSurveyEntries(userId),
        getUserOrganizations(userId, userData.isSuperAdmin),
      ]);

      results.forEach((result, index) => {
        if (result.status === "rejected") {
          console.error(
            `Promise at index ${index} was rejected with reason:`,
            result.reason,
          );
        }
      });

      const [likes, comments, surveyEntries, organizations] = results.map(
        (result) => (result.status === "fulfilled" ? result.value : []),
      );

      userData.likes = likes;
      userData.comments = comments;
      userData.surveyEntries = surveyEntries;
      userData.organizations = organizations;
    }
    return userData;
  };

  const sendVerification = {
    sendEmailVerification: false,
    emailVerificationOptions: {
      // url: `${window.location.protocol}//${window.location.host}/verify`,
      url: window.location.href,
    },
  };
  const signInWith = useSignIn(auth, db);
  const createUserWithEmailAndPassword = useCreateUserWithEmailAndPassword(
    auth,
    db,
    sendVerification,
  );
  const submitEditDetails = useHandleSubmitEditDetails(user || {});

  // Handler
  const handler: AuthMethods = {
    signIn({ provider, formikStore, id, project }) {
      if (!formikStore) throw new Error("No formikStore provided to signIn()");
      setErrorMessage(blankError);
      return new Promise((resolve, reject) => {
        switch (provider) {
          case "apple":
          case "facebook":
          case "google":
            setLoading(id || provider);
            signInWith
              .provider(provider, ["email"], formikStore)
              .then((user) => {
                getUserData(user.uid, project)
                  .then((userData) => {
                    setUser(userData);
                    resolve(userData);
                  })
                  .catch((error) => {
                    handleSetErrorMessage(error);
                    reject(error);
                  });
              })
              .catch((err) => {
                handleSetErrorMessage(err);
                reject(generateErrorMessage(err?.code));
              })
              .finally(() => setLoading(false));
            break;
          case "email":
            setLoading("email");
            signInWith
              .email(formikStore)
              .then((user) => {
                getUserData(user.uid, project)
                  .then((userData) => {
                    setUser(userData);
                    resolve(userData);
                  })
                  .catch((error) => {
                    handleSetErrorMessage(error);
                    reject(error);
                  });
              })
              .catch((err) => {
                handleSetErrorMessage(err);
                reject(generateErrorMessage(err?.code));
              })
              .finally(() => setLoading(false));
            break;
          case "anonymous":
            setLoading("anonymous");
            signInWith
              .anonymous(formikStore)
              .then((user) => {
                getUserData(user.uid, project)
                  .then((userData) => {
                    setUser(userData);
                    resolve(userData);
                  })
                  .catch((error) => {
                    handleSetErrorMessage(error);
                    reject(error);
                  });
              })
              .catch((err) => {
                handleSetErrorMessage(err);
                reject(generateErrorMessage(err?.code));
              })
              .finally(() => setLoading(false));
            break;

          default:
            throw new Error("No provider provided to signIn");
        }
      });
    },
    signOut: async () => {
      signOut(auth)
        .then(() => setUser(false))
        .finally(() => setLoading(false));
    },
    deleteUserFromDb: async () => {
      const currentuser = auth.currentUser;

      if (!currentuser) throw Error("User is not logged in");

      try {
        const cantDeleteSuperAdmins = "dein@senf.koeln";
        if (cantDeleteSuperAdmins === currentuser.email) {
          throw new Error("Can't delete SuperAdmin");
        }

        await deleteUser(currentuser);
        return { success: true };
      } catch (error) {
        if (error.code === "auth/requires-recent-login") {
          const error = "auth/requires-recent-login";
          return { error };
        }
        throw new Error(error, "error in deleteUserFromDb");
      }
    },
    getUserData,
    getUserIdeas,
    getUserOrganizations,
    getUserSurveyEntries,
    hideIdea,
    unHideIdea,
    async submitComment(...props) {
      const comment = await submitComment(...props);
      setUser((prevUser) => {
        if (!prevUser)
          throw new Error(
            "No user found in  auth state trying to submit comment",
          );
        return { ...prevUser, comments: [...prevUser.comments, comment] };
      });

      return comment;
    },
    async deleteComment(...props) {
      const deletedCommentId = await deleteComment(...props);
      const commentIndex = user?.comments?.findIndex(
        ({ commentId }: { commentId: string }) =>
          commentId === deletedCommentId,
      );
      if (commentIndex > -1) {
        setUser((prevUser) => {
          if (!prevUser)
            throw new Error(
              "No user found in  auth state trying to delete comment",
            );
          const updatedComments = [...prevUser.comments];
          updatedComments.splice(commentIndex, 1);
          return { ...prevUser, comments: updatedComments };
        });
      }
      return deletedCommentId;
    },
    async handleLike(ideaId: string) {
      return new Promise((resolve) => {
        const ideaIndex = user?.likes?.findIndex(
          (like) => like.ideaId === ideaId,
        );
        if (ideaIndex > -1) {
          setUser((prevUser) => {
            if (!prevUser)
              throw new Error(
                "No user found in  auth state trying to delete like",
              );
            const updatedLikes = [...prevUser.likes];
            updatedLikes.splice(ideaIndex, 1);
            return { ...prevUser, likes: updatedLikes };
          });
          resolve(false);
          return;
        }
        const newLike = {
          ideaId,
          userHandle: user?.handle,
          userId: user?.userId,
        };
        setUser((prevUser) => {
          if (!prevUser)
            throw new Error("No user found in  auth state trying to add like");
          return { ...prevUser, likes: [...prevUser.likes, newLike] };
        });
        resolve(newLike);
      });
    },
    async createUser(formikRegisterStore) {
      return new Promise((resolve, reject) => {
        setLoading("email");
        createUserWithEmailAndPassword(formikRegisterStore)
          .then(({ user }) => {
            getUserData(user?.uid).then((userData) => {
              setUser(userData);
              resolve(userData);
            });
          })
          .catch((err) => {
            handleSetErrorMessage(err);
            reject(generateErrorMessage(err?.code));
          })
          .finally(() => setLoading(false));
      });
    },

    async sendPasswordResetEmail(email, actionCodeSettings) {
      return new Promise((resolve, reject) => {
        setLoading("reset");
        sendPasswordResetEmail(auth, email, actionCodeSettings)
          .then(resolve)
          .catch((err) => {
            handleSetErrorMessage(err);
            reject(generateErrorMessage(err?.code));
          })
          .finally(() => setLoading(false));
      });
    },
    sendEmailVerification,
    async userExists(formikStore, login = true) {
      setLoading("email");
      try {
        const { email } = formikStore.values;
        const signInMethod = await fetchSignInMethodsForEmail(auth, email);
        if (!login) return signInMethod;
        if (signInMethod?.includes("google.com"))
          handler.signIn({ provider: "google", formikStore });
        if (signInMethod?.includes("facebook.com"))
          handler.signIn({ provider: "facebook", formikStore }); // do we need to return here too ?
        return signInMethod;
      } catch (err) {
        handleSetErrorMessage(err);
        return [];
      } finally {
        setLoading(false);
      }
    },
    submitEditDetails: async ({ formikStore, project }) => {
      return new Promise((resolve, reject) => {
        setLoading("edit");
        submitEditDetails(formikStore, project)
          .then((participant) => {
            getUserData(participant?.userId, project, participant).then(
              (userData) => {
                setUser(userData);
                resolve(userData);
              },
            );
          })
          .catch((err) => {
            handleSetErrorMessage(err);
            reject(generateErrorMessage(err?.code));
          })
          .finally(() => setLoading(false));
      });
    },
    handleImageUpload: async (event, targetUser) => {
      const currentUser =
        !targetUser && auth.currentUser ? auth.currentUser : targetUser;
      if (!currentUser) throw Error("User is not logged in");
      if (
        auth.currentUser?.uid !== currentUser.uid &&
        currentUser.isAdmin === false &&
        currentUser.isSuperAdmin === false &&
        currentUser.isModerator === false
      ) {
        throw new Error("user not authorized to handleImageUpload");
      }
      const target = event?.target as HTMLInputElement;
      if (!target?.files?.length) {
        throw new Error("no file selected");
      }
      const imageFile = target.files[0];
      // console.log("originalFile instanceof Blob", imageFile instanceof Blob); // true
      // console.log(`originalFile size ${imageFile.size / 1024 / 1024} MB`);

      const options = {
        maxSizeMB: 0.03,
        maxWidthOrHeight: 700,
        useWebWorker: true,
        fileType: "image/jpeg",
      };
      try {
        const compressedFile = await imageCompression(imageFile, options);
        const storageRef = ref(
          getStorage(),
          `profileImages/${currentUser.uid}/thumbnail`,
        );
        const userRef = doc(db, `users/${currentUser.uid}`);

        await uploadBytes(storageRef, compressedFile);
        const photoURL = await getDownloadURL(storageRef);
        await updateDoc(userRef, { photoURL });
        return photoURL;
      } catch (error) {
        throw new Error(error);
      }
    },
  };

  const logUserRetention = async (user: User) => {
    if (!user) return;
    try {
      const userInDb = await getDoc(doc(db, `users/${user.uid}`));
      if (!userInDb.exists()) return;
      const { projectId } = userInDb.data();
      const now = new Date().getTime();

      const visitHistoryRef = collection(
        db,
        "users",
        user.uid,
        "activityLogbook",
      );
      const visitData = {
        userId: user.uid,
        visitorId: localStorage.getItem("visitorId") || null,
        projectId: projectId || null,
        timeStamp: now,
        createdAt: new Date().toISOString(),
        url: window.location.href,
        referrer: localStorage.getItem("referrer") || null,
        device: isMobileOnly
          ? "mobile"
          : isTablet
            ? "tablet"
            : isDesktop
              ? "desktop"
              : "other",
      };
      await addDoc(visitHistoryRef, visitData);
    } catch (error) {
      throw new Error(`${error} logUserRetention`);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (!authUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      getUserData(authUser.uid)
        .then(setUser)
        .catch(() => setUser(null))
        .finally(() => setLoading(false));

      await logUserRetention(authUser);
    });

    return () => unsubscribe();
  }, []);

  React.useEffect(() => {
    const timout = setTimeout(() => setErrorMessage(blankError), 20000);
    return () => clearTimeout(timout);
  }, [errorMessage]);

  // if (loading) {
  //   return (
  //     <Box
  //       justifyContent="center"
  //       alignItems="center"
  //       flexDirection="column"
  //       height="100%"
  //       width="100%"
  //     >
  //       <div style={{ width: "200px" }}>
  //         <Loader />
  //       </div>
  //     </Box>
  //   );
  // }

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        setLoading,
        errorMessage,
        ...handler,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => React.useContext(AuthContext);
export default AuthProvider;
